import { Component, HostBinding, signal, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';

import {
  ProgressComponent,
  IconComponent,
  MessageService
} from '@factor_ec/ui';

import { AppService } from '@/core/app.service';
import { ApiService } from '@/core/api.service';
import { RequestData } from '@/core/models/request-data';

/**
 * @description Componente para restablecer la contraseña del usuario
 * @class ResetPasswordComponent
 */
@Component({
  selector: 'app-reset-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormField,
    MatInputModule,
    IconComponent,
    ProgressComponent
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordComponent implements OnInit {
  /** @description Servicio principal de la aplicación */
  appService = inject(AppService);

  /** @description Servicio de API */
  private apiService = inject(ApiService);

  /** @description Constructor de formularios */
  private formBuilder = inject(FormBuilder);

  /** @description Servicio de mensajes */
  private messageService = inject(MessageService);

  /** @description Ruta activa */
  private route = inject(ActivatedRoute);

  /** @description Servicio de enrutamiento */
  private router = inject(Router);

  /** @description Servicio de título */
  private title = inject(Title);

  /** @description Formulario reactivo */
  form: FormGroup = new FormGroup({});

  /** @description Señal de envío en proceso */
  submitting = signal<boolean>(false);

  /** @description Mensaje de contraseñas no coincidentes */
  notEqualMessage = $localize`New password is not the same`;

  /** @description Señal para la visibilidad de la confirmacion de contraseña */
  confirmPasswordVisible = signal<boolean>(false);

  /** @description Señal para la visibilidad de la contraseña */
  passwordVisible = signal<boolean>(false);

  /** @description Clase CSS del componente */
  class: string = '';

  /** @description Clases CSS del host */
  @HostBinding('class') get hostClasses(): string {
    return ['ft-auth', 'ft-auth--form', this.class].join(' ');
  }

  /**
   * @description Constructor del componente
   * @constructor
   */
  constructor() {
    this.title.setTitle($localize`Reset password`);
  }

  ngOnInit(): void {
    this.initForm();
  }

  /**
   * @description Validador de confirmación de contraseña
   * @param {AbstractControl} control - Control a validar
   * @returns {ValidationErrors | null} Errores de validación
   */
  confirmPasswordValidator(
    control: AbstractControl
  ): { [key: string]: any } | null {
    let value: { [key: string]: any } | null = null;
    if (
      control &&
      control.parent &&
      control.parent.get('password')?.value !== control.value
    ) {
      value = { notEqual: true, fieldName: $localize`New password` };
    }
    return value;
  }

  /**
   * @description Inicializa el formulario reactivo
   * @private
   */
  private initForm(): void {
    this.form = this.formBuilder.group({
      validationCode: this.route.snapshot.queryParamMap.get('validationCode'),
      password: ['', [Validators.required, this.passwordValidator()]],
      confirmPassword: [
        '',
        [Validators.required, this.confirmPasswordValidator]
      ]
    });
    this.form.get('password')?.valueChanges.subscribe(value => {
      this.form.get('confirmPassword')?.updateValueAndValidity();
    });
  }

  /**
   * @description Validador de contraseña
   * @returns {ValidatorFn} Función validadora
   */
  passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value || '';

      const hasMinLength = value.length >= 8;
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasSpecialCharacter = /[-_!@#$%^&*(),.?":{}|<>]/.test(value);
      const hasNumber = /\d/.test(value);

      const errors: any = {};
      if (!hasMinLength)
        errors.minLength = $localize`Must contain at least 8 characters`;
      if (!hasUpperCase)
        errors.upperCase = $localize`Must contain at least 1 capital letter`;
      if (!hasLowerCase)
        errors.lowerCase = $localize`Must contain at least 1 lowercase letter`;
      if (!hasSpecialCharacter)
        errors.specialCharacter = $localize`Must contain at least 1 special character`;
      if (!hasNumber) errors.number = $localize`Must contain 1 number`;

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  /**
   * @description Envía el formulario para restablecer la contraseña
   * @returns {Promise<void>}
   */
  async submit(): Promise<void> {
    if (this.form.valid) {
      try {
        const data: RequestData = {
          microServiceName: 'WSMPUsersManagement',
          resourcePath: `/user/recovery-password`,
          method: 'POST',
          useToken: true,
          body: {
            validationCode: this.form.value.validationCode,
            password: this.form.value.password
          },
          bodyEncrypted: true
        };
        this.submitting.set(true);
        this.form.disable();
        await this.apiService.call(data);
        this.submitting.set(false);
        this.router.navigateByUrl('/');
        this.messageService.show(
          $localize`Your password was changed successfully.`,
          { class: 'ft-message--success', icon: 'check--circle' }
        );
      } catch (err: any) {
        this.submitting.set(false);
        this.form.enable();
      }
    }
  }

  /**
   * @description Alterna la visibilidad de la contraseña
   */
  togglePasswordVisible(): void {
    this.passwordVisible.set(!this.passwordVisible());
  }

  /**
   * @description Alterna la visibilidad de la confirmación decontraseña
   */
  toggleConfirmPasswordVisible(): void {
    this.confirmPasswordVisible.set(!this.confirmPasswordVisible());
  }
}
